import React, { useState } from 'react';
import { PLANS } from '../lib/pricingConfig';

/*
  PricingPage — dedicated pricing page.
  Consumes shared PLANS from src/lib/pricingConfig.js.
  Props: navigateTo, user
*/
const PricingPage = ({ navigateTo, user }) => {
  const [earlyAccessEmail, setEarlyAccessEmail] = useState('');
  const [earlyAccessSubmitted, setEarlyAccessSubmitted] = useState(false);

  const resolveOnCta = (plan) => {
    if (plan.id === 'expert') return () => navigateTo('professionals-directory');
    return () => navigateTo(user ? (user.accountType === 'personal' ? 'personal-dashboard' : 'dashboard') : 'signup');
  };

  const resolveCta = (plan) => {
    if (plan.id === 'expert') return 'Connect now';
    return plan.cta;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>

      {/* Header */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 24px 48px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 16px' }}>Pricing</p>
        <h1 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2, margin: '0 0 16px', letterSpacing: '-0.025em' }}>
          Simple, honest pricing.
        </h1>
        <p style={{ fontSize: '17px', color: 'var(--text-secondary)', lineHeight: 1.65, margin: 0, maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
          Start free. Upgrade when you need more. Connect with a professional for complex matters.
        </p>
      </div>

      {/* Pricing cards */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px 80px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          alignItems: 'start',
        }}>
          {PLANS.map((plan) => {
            const { id, name, price, period, priceNote, desc, features, highlight, badge } = plan;
            const cta = resolveCta(plan);
            const onCta = resolveOnCta(plan);
            return (
              <div
                key={id}
                style={{
                  backgroundColor: 'var(--bg)',
                  border: highlight ? '2px solid var(--accent)' : '1px solid var(--border)',
                  borderRadius: '16px',
                  padding: '32px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: highlight ? 'var(--shadow-lg)' : 'var(--shadow-card)',
                }}
              >
                {badge && (
                  <span style={{
                    position: 'absolute', top: '-1px', right: '24px',
                    backgroundColor: 'var(--accent)', color: 'var(--bg)',
                    fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', padding: '4px 10px',
                    borderRadius: '0 0 6px 6px',
                  }}>{badge}</span>
                )}

                {/* Tier name */}
                <p style={{
                  fontSize: '11px', fontWeight: 700,
                  color: highlight ? 'var(--accent)' : 'var(--text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 20px',
                }}>{name}</p>

                {/* Price */}
                <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{ fontSize: '38px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>{price}</span>
                  {period && <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{period}</span>}
                </div>
                {priceNote && <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: '0 0 8px' }}>{priceNote}</p>}
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>{desc}</p>

                {/* CTA */}
                <button
                  onClick={onCta}
                  style={{
                    width: '100%', padding: '12px 20px', borderRadius: '10px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'inherit', transition: 'background-color 0.2s, border-color 0.2s',
                    marginBottom: '28px',
                    ...(highlight
                      ? { backgroundColor: 'var(--accent)', color: 'var(--bg)', border: '1px solid var(--accent)' }
                      : { backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)' }
                    ),
                  }}
                  onMouseEnter={e => {
                    if (highlight) e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
                    else e.currentTarget.style.borderColor = 'var(--text-tertiary)';
                  }}
                  onMouseLeave={e => {
                    if (highlight) e.currentTarget.style.backgroundColor = 'var(--accent)';
                    else e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >{cta}</button>

                {/* Feature list */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{ color: highlight ? 'var(--accent)' : 'var(--text-tertiary)', fontSize: '14px', lineHeight: 1, marginTop: '2px', flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Small print */}
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: '28px', lineHeight: 1.6 }}>
          All prices in INR. Expert Connect billed per matter. No subscription required for Expert Connect.
        </p>
      </div>

      {/* Early access section */}
      <div style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '72px 24px' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 16px' }}>Early access</p>
          <h2 style={{ fontSize: 'clamp(22px, 3vw, 34px)', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3, margin: '0 0 12px' }}>
            Join early access
          </h2>
          <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.65, margin: '0 0 32px' }}>
            Be among the first to access Pro features. No credit card required.
          </p>

          {earlyAccessSubmitted ? (
            <div style={{ padding: '16px 24px', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(0,194,124,0.2)', borderRadius: '10px' }}>
              <p style={{ fontSize: '14px', color: 'var(--accent)', fontWeight: 600, margin: 0 }}>You're on the list! We'll be in touch soon.</p>
            </div>
          ) : (
            <form
              onSubmit={e => { e.preventDefault(); if (earlyAccessEmail.trim()) setEarlyAccessSubmitted(true); }}
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <input
                type="email"
                placeholder="your@email.com"
                value={earlyAccessEmail}
                onChange={e => setEarlyAccessEmail(e.target.value)}
                style={{
                  flex: '1', minWidth: '220px', maxWidth: '300px', padding: '12px 16px',
                  borderRadius: '9px', border: '1px solid var(--border)',
                  backgroundColor: 'var(--bg)', color: 'var(--text-primary)',
                  fontSize: '14px', outline: 'none', fontFamily: 'inherit',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
              />
              <button
                type="submit"
                style={{
                  padding: '12px 24px', borderRadius: '9px', backgroundColor: 'var(--accent)',
                  color: 'var(--bg)', border: 'none', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
              >Get early access</button>
            </form>
          )}
        </div>
      </div>

      {/* Footer trust strip */}
      <div style={{ backgroundColor: 'var(--bg)', padding: '32px 24px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
          {[
            'Your data is encrypted at rest and in transit',
            'We never sell your data to third parties',
            'Built for Indian financial context',
          ].map(text => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: 'var(--accent)', lineHeight: 1 }}>✓</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default PricingPage;
